import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { getOptimizedImageSrcSet, getOptimizedImageUrl } from '@/lib/imageOptimization';

const preloadedGalleryImages = new Set<string>();

interface VenueImageGalleryProps {
  venueName: string;
  href: string;
  images: string[];
  aspectClassName?: string;
  className?: string;
  imageClassName?: string;
  showPhotoCount?: boolean;
  disableLink?: boolean;
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  bottomContent?: React.ReactNode;
}

export function VenueImageGallery({
  venueName,
  href,
  images,
  aspectClassName = 'aspect-square',
  className = '',
  imageClassName = '',
  showPhotoCount = true,
  disableLink = false,
  topLeft,
  topRight,
  bottomContent,
}: VenueImageGalleryProps) {
  const galleryImages = React.useMemo(
    () => Array.from(new Set(images.filter(Boolean))).slice(0, 5),
    [images],
  );
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const touchStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const touchLastRef = React.useRef<{ x: number; y: number } | null>(null);
  const canSlideImages = galleryImages.length > 1;
  const activeImage = galleryImages[activeImageIndex] ?? galleryImages[0] ?? '';
  const activeImageSrc = React.useMemo(
    () => getOptimizedImageUrl(activeImage, { width: 640, quality: 'auto:good', fit: 'fill' }),
    [activeImage],
  );
  const activeImageSrcSet = React.useMemo(
    () => getOptimizedImageSrcSet(activeImage, [420, 640, 900, 1200], { quality: 'auto:good', fit: 'fill' }),
    [activeImage],
  );

  React.useEffect(() => {
    setActiveImageIndex(0);
  }, [href, images]);

  React.useEffect(() => {
    if (galleryImages.length <= 1 || typeof window === 'undefined') return;

    const indicesToPreload = [
      activeImageIndex,
      (activeImageIndex + 1) % galleryImages.length,
      (activeImageIndex - 1 + galleryImages.length) % galleryImages.length,
    ];

    indicesToPreload.forEach((index) => {
      const nextImage = galleryImages[index];
      if (!nextImage) return;

      const preloadSrc = getOptimizedImageUrl(nextImage, { width: 900, quality: 'auto:good', fit: 'fill' });
      if (!preloadSrc || preloadedGalleryImages.has(preloadSrc)) return;

      preloadedGalleryImages.add(preloadSrc);
      const image = new window.Image();
      image.decoding = 'async';
      image.src = preloadSrc;
    });
  }, [activeImageIndex, galleryImages]);

  const goToImage = (nextIndex: number) => {
    setActiveImageIndex((nextIndex + galleryImages.length) % galleryImages.length);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch) return;

    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchLastRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch || touchStartRef.current === null) return;

    touchLastRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!canSlideImages || touchStartRef.current === null) return;

    const endTouch = event.changedTouches[0];
    const end = touchLastRef.current ?? (
      endTouch ? { x: endTouch.clientX, y: endTouch.clientY } : null
    );

    if (!end) {
      touchStartRef.current = null;
      touchLastRef.current = null;
      return;
    }

    const deltaX = touchStartRef.current.x - end.x;
    const deltaY = touchStartRef.current.y - end.y;
    touchStartRef.current = null;
    touchLastRef.current = null;

    if (Math.abs(deltaX) < 56) return;
    if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

    goToImage(activeImageIndex + (deltaX > 0 ? 1 : -1));
  };

  const galleryContent = (
    <>
      <img
        src={activeImageSrc}
        srcSet={activeImageSrcSet}
        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
        alt={venueName}
        className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] ${imageClassName}`}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/52 via-black/8 to-transparent" />
      {topLeft ? <div className="absolute left-2.5 top-2.5">{topLeft}</div> : null}
      {(topRight || (showPhotoCount && galleryImages.length > 1)) ? (
        <div className="absolute right-2.5 top-2.5 flex flex-col items-end gap-1.5">
          {topRight}
          {showPhotoCount && galleryImages.length > 1 ? (
            <div className="inline-flex items-center rounded-full border border-white/16 bg-black/28 px-2 py-1 text-[9px] font-semibold text-white/80 backdrop-blur-md">
              {galleryImages.length} photos
            </div>
          ) : null}
        </div>
      ) : null}
      {bottomContent ? <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">{bottomContent}</div> : null}
    </>
  );

  return (
    <div
      className={`group relative overflow-hidden ${aspectClassName} ${className}`}
      style={{ touchAction: canSlideImages ? 'pan-y' : 'auto' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {disableLink ? (
        <div className="block h-full w-full">
          {galleryContent}
        </div>
      ) : (
        <Link to={href} className="block h-full w-full">
          {galleryContent}
        </Link>
      )}

      {canSlideImages ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[1] flex justify-center">
            <div className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-black/28 px-2 py-1 backdrop-blur-md">
              {galleryImages.map((image, dotIndex) => (
                <button
                  key={`${image}-${dotIndex}`}
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    goToImage(dotIndex);
                  }}
                  className={`h-1.5 rounded-full transition-all ${
                    dotIndex === activeImageIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/52'
                  }`}
                  aria-label={`Show image ${dotIndex + 1} for ${venueName}`}
                />
              ))}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-[1] hidden items-center justify-between px-2 md:flex">
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToImage(activeImageIndex - 1);
              }}
              className="pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/18 bg-black/28 text-white/88 opacity-0 backdrop-blur-md transition-all group-hover:opacity-100 hover:bg-black/42"
              aria-label={`Previous image for ${venueName}`}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToImage(activeImageIndex + 1);
              }}
              className="pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/18 bg-black/28 text-white/88 opacity-0 backdrop-blur-md transition-all group-hover:opacity-100 hover:bg-black/42"
              aria-label={`Next image for ${venueName}`}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
